import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { cn } from "@/lib/utils";
import { Tier } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";

import { EmptyRewardCard, isPlayerPending } from "./RewardPlayerCard";
import { SBCRewardDescription } from "./SBCRewardDescription/SBCRewardDescription";
import { TradeableChip } from "./TradeableChip";

type Props = {
  reward?: SBCRewardType;
  rewardUrl: string;
  description?: string;
  tier?: Tier;
  /** Divided off at the right edge of the band: the set card's vote chip. */
  trailing?: React.ReactNode;
  className?: string;
  /** Player rewards only: clicking the card opens the player. */
  onViewPlayer?: () => void;
};

/**
 * What the SBC pays out, across the foot of the card, under a gold label and
 * the chip that says whether it can be sold on.
 */
export const RewardBand = ({
  reward,
  rewardUrl,
  description,
  tier,
  trailing,
  className,
  onViewPlayer,
}: Props) => {
  const { t } = useTranslation("sbcs");

  const band = cn("flex items-center gap-x-4 px-5 py-4", className);

  /* A player card stands ~23px taller than a pack, and the card it sits in is
     sized off whichever card in the row is tallest — so it pays for itself and
     for its neighbours. It pulls up into the slack above instead. */
  const playerBand = cn("-mt-8", band);

  if (!reward) {
    return (
      <div className={band}>
        <p className="text-sm text-foreground-body">{t("set.noReward")}</p>
      </div>
    );
  }

  const isPlayerCard = reward.type === "player" && !!reward.player;

  return (
    <div className={isPlayerCard ? playerBand : band}>
      {isPlayerCard ? (
        <div className="shrink-0 drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]">
          {isPlayerPending(reward.player) ? (
            <EmptyRewardCard scale={0.6} />
          ) : (
            <div
              className={cn(
                onViewPlayer &&
                  "cursor-pointer transition-transform duration-200 hover:scale-104",
              )}
              // The set card is one big link, so the click calls off its navigation.
              onClick={
                onViewPlayer &&
                ((e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewPlayer();
                })
              }
            >
              <PlayerCard
                player={reward.player}
                position={reward.player.preferredPosition}
                scale={0.6}
                hidePrice
              />
            </div>
          )}
        </div>
      ) : (
        <img
          loading="lazy"
          className="w-20 shrink-0 drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
          alt={reward.name}
          src={rewardUrl}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-card-gold">
            {t("set.rewardBand")}
          </span>
          <TradeableChip
            rewardType={reward.type}
            untradeable={reward.untradeable}
          />
        </div>
        {/* Under the label, above the words: where the rating read before the
            card took over the artwork. */}
        {reward.metaRating?.metarating != null && (
          <div className="w-fit">
            <MetaRatingChip
              metaRating={reward.metaRating.metarating}
              tier={tier ?? undefined}
            />
          </div>
        )}
        <SBCRewardDescription
          rewardType={reward.type}
          description={reward.description || description}
          rewardName={reward.name || reward.shortName}
        />
      </div>
      {trailing && <div className="shrink-0 pl-4">{trailing}</div>}
    </div>
  );
};
